import {
    useCallback,
} from 'react'

import AccountLaneForm from 'app/Apps/AccountManagement/AccountLanes/AccountLaneForm'
import Button from 'app/shared-components/Button'
import useGetAccountLane from 'app/Apps/AccountManagement/AccountLanes/hooks/useGetAccountLane'
import request from 'app/requests/Lane/Lane.requests'
import {
    LaneStatus,
} from 'app/enums/label.enums'
import SkyNetLink, {
    SizeLink, TypeLink,
} from 'app/shared-components/SkyNetLink'
import StatusHandler from 'app/shared-components/StatusHandler'
import useContactManagementRoutes from 'app/SkyNetRoutes/hooks/useContactManagementRoutes'
import {
    generateSkyNetLink,
} from 'app/SkyNetRoutes'
import useHasLocationPermissionType from 'app/Apps/ContactManagement/utils/useHasLocationPermissionType'

import useRejectPricingLane from 'app/Apps/AccountManagement/AQuotes/hooks/useRejectPricingLane'
import useCancelLane from '../../hooks/useCancelLane'
import getLaneToBeGroupedFields from './LaneToBeGroupedUpdate.fields'
import useStyles from './LaneToBeGroupedUpdate.styles'
import useAssignLaneToLaneGroup from './hooks/useAssignLaneToLaneGroup'

const LaneToBeGroupedUpdate = ({
    id,
    refetch,
}: {
    id: number,
    refetch: (...args: any[]) => void
}) => {
    const {
        Locations: LocationsRoutes,
    } = useContactManagementRoutes()

    const {
        classes,
    } = useStyles()
    const {
        data,
        isDataReady,
        isFetching,
        isError,
        error,
        invalidate,
    } = useGetAccountLane(id)

    const onSuccess = useCallback(() => {
        invalidate()
        refetch()
    }, [
        invalidate,
        refetch,
    ])

    const reject = useRejectPricingLane({
        id,
        onSuccess,
    })

    const assignToLaneGroup = useAssignLaneToLaneGroup({
        id,
        onSuccess,
    })

    const cancelLane = useCancelLane({
        id,
        onSuccess,
    })

    const hasCreateLocationPermissions = useHasLocationPermissionType([], 'create')

    const getAddressCreationLink = useCallback(() => {
        return hasCreateLocationPermissions ? [<SkyNetLink
            link={generateSkyNetLink({
                domainPath: LocationsRoutes.Create.path,
            })}
            text="Create Address"
            type={TypeLink.EXTERNAL}
            size={SizeLink.S}
        />] : undefined
    }, [
        hasCreateLocationPermissions,
        LocationsRoutes,
    ])

    const getCustomButtons = useCallback(({
        isValid, synced,
    }) => {
        return [
            <Button
                key="assing-to-lane-group"
                label="Assign To Group"
                onClick={assignToLaneGroup}
                disabled={!isValid || !synced}
            />,
            <Button
                key="reject"
                label="Reject"
                onClick={reject}
                disabled={data?.laneStatus !== LaneStatus.UNDER_REVIEW_PRICING}
            />,
            <Button
                key="cancelLane"
                label="Cancel Lane"
                onClick={cancelLane}
            />,
        ]
    }, [
        assignToLaneGroup,
        reject,
        data?.laneStatus,
        cancelLane,
    ])

    return (
        <div className={classes.formContainer}>
            <StatusHandler
                isSuccess={isDataReady}
                isFetching={isFetching}
                isError={isError}
                error={error}
            >
                <AccountLaneForm
                    data={data}
                    requestParam={request.quote({
                        id,
                    })}
                    onSuccess={onSuccess}
                    onCancel={invalidate}
                    getCustomButtons={getCustomButtons}
                    getCustomFields={getLaneToBeGroupedFields}
                    getAddressCreationLink={getAddressCreationLink}
                    customClasses={classes}
                    exists
                />
            </StatusHandler>
        </div>
    )
}

export default LaneToBeGroupedUpdate
